import { router } from "@inertiajs/react";
import { ArrowRight, LoaderIcon, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type {
	LocationMode,
	RoutingEnv,
	RoutingLocationPolicy,
} from "@/types/admin-portal/routing-mode";
import { decimalText, parseOptionalNumber, sendRouting } from "./api";
import { LocationModeSelect, ReasonField } from "./fields";
import { calibrationErrors, locationChanges } from "./locations";

function afterWrite(changed: boolean, t: (key: string) => string) {
	toast.success(
		changed ? t("messages.saved_propagation") : t("messages.no_change"),
	);
	router.reload({ only: ["locations"] });
}

export function EditLocationDialog({
	location,
	env,
	url,
	onClose,
}: {
	location: RoutingLocationPolicy;
	/** the backend env; a location setting applies to it alone */
	env?: RoutingEnv;
	url: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("routing-mode");
	const [mode, setMode] = useState<LocationMode>(location.mode);
	const [factor, setFactor] = useState(decimalText(location.circuityFactor));
	const [speed, setSpeed] = useState(decimalText(location.speedKmh));
	const [notes, setNotes] = useState(location.notes ?? "");
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);

	const factorValue = parseOptionalNumber(factor);
	const speedValue = parseOptionalNumber(speed);
	const { factor: factorInvalid, speed: speedInvalid } = calibrationErrors(
		factorValue,
		speedValue,
	);
	const changes = locationChanges(location, {
		mode,
		factor: factorValue,
		speed: speedValue,
		notes,
	});

	const hasChanges = Object.keys(changes).length > 0;
	const uncalibratedHaversine = mode === "HAVERSINE" && factorValue === null;
	const canSave = hasChanges && !factorInvalid && !speedInvalid && !saving;

	async function onSave() {
		setSaving(true);
		const result = await sendRouting<{ changed: boolean }>(
			url,
			"PUT",
			{ ...changes, reason: reason.trim() || undefined },
			t("messages.error"),
		);
		setSaving(false);
		if (!result.ok) {
			toast.error(result.error);
			return;
		}
		onClose();
		afterWrite(result.data.changed, t);
	}

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t("locations.edit.title", { city: location.city })}
					</DialogTitle>
					<DialogDescription>
						{t("locations.edit.description", { env: env ?? "—" })}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<Label>{t("global.mode")}</Label>
						<LocationModeSelect value={mode} onChange={setMode} />
					</div>

					{uncalibratedHaversine && (
						<Alert className="border-amber-500 text-amber-700 dark:text-amber-400">
							<TriangleAlert className="size-4" />
							<AlertTitle>{t("locations.edit.uncalibrated.title")}</AlertTitle>
							<AlertDescription>
								{t("locations.edit.uncalibrated.body")}
							</AlertDescription>
						</Alert>
					)}

					<div className="grid gap-4 sm:grid-cols-2">
						<div className="flex flex-col gap-2">
							<Label htmlFor="routing-factor">
								{t("locations.columns.factor")}
							</Label>
							<Input
								className="bg-input shadow-inner"
								id="routing-factor"
								type="text"
								inputMode="decimal"
								value={factor}
								placeholder="1,4"
								aria-invalid={factorInvalid}
								onChange={(e) => setFactor(e.target.value)}
							/>
							<p
								className={
									factorInvalid
										? "text-xs text-destructive"
										: "text-xs text-muted-foreground"
								}
							>
								{t("locations.edit.factor_hint")}
							</p>
						</div>
						<div className="flex flex-col gap-2">
							<Label htmlFor="routing-speed">
								{t("locations.columns.speed")}
							</Label>
							<Input
								className="bg-input shadow-inner"
								id="routing-speed"
								type="text"
								inputMode="decimal"
								value={speed}
								placeholder="22,5"
								aria-invalid={speedInvalid}
								onChange={(e) => setSpeed(e.target.value)}
							/>
							<p
								className={
									speedInvalid
										? "text-xs text-destructive"
										: "text-xs text-muted-foreground"
								}
							>
								{t("locations.edit.speed_hint")}
							</p>
						</div>
					</div>

					<div className="flex flex-col gap-2">
						<Label htmlFor="routing-notes">{t("locations.edit.notes")}</Label>
						<Textarea
							className="bg-input shadow-inner field-sizing-content min-h-16 max-h-40 resize-none"
							id="routing-notes"
							value={notes}
							maxLength={2000}
							onChange={(e) => setNotes(e.target.value)}
						/>
					</div>

					<ReasonField
						id="routing-location-reason"
						value={reason}
						onChange={setReason}
					/>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={saving}>
						{t("actions.cancel")}
					</Button>
					<Button onClick={onSave} disabled={!canSave}>
						{saving && <LoaderIcon className="size-4 animate-spin" />}
						{t("actions.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function ResetLocationDialog({
	location,
	url,
	onClose,
}: {
	location: RoutingLocationPolicy;
	url: string;
	onClose: () => void;
}) {
	const { t } = useTranslation("routing-mode");
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);

	async function onReset() {
		setSaving(true);
		const result = await sendRouting<{ changed: boolean }>(
			url,
			"DELETE",
			{ reason: reason.trim() || undefined },
			t("messages.error"),
		);
		setSaving(false);
		if (!result.ok) {
			toast.error(result.error);
			return;
		}
		onClose();
		afterWrite(result.data.changed, t);
	}

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t("locations.reset.title", { city: location.city })}
					</DialogTitle>
					<DialogDescription>
						{location.calibrated
							? t("locations.reset.body_calibrated")
							: t("locations.reset.body")}
					</DialogDescription>
				</DialogHeader>

				<ReasonField
					id="routing-reset-reason"
					value={reason}
					onChange={setReason}
				/>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={saving}>
						{t("actions.cancel")}
					</Button>
					<Button variant="destructive" onClick={onReset} disabled={saving}>
						{saving && <LoaderIcon className="size-4 animate-spin" />}
						{t("locations.reset.confirm")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

// PUT /locations/bulk caps a list at 500 items.
export const BULK_MAX = 500;

export function BulkApplyDialog({
	locations,
	env,
	url,
	onClose,
	onDone,
}: {
	locations: RoutingLocationPolicy[];
	env?: RoutingEnv;
	url: string;
	onClose: () => void;
	onDone: () => void;
}) {
	const { t } = useTranslation("routing-mode");
	const [mode, setMode] = useState<LocationMode>("HAVERSINE");
	const [reason, setReason] = useState("");
	const [saving, setSaving] = useState(false);

	const uncalibrated = locations.filter((l) => !l.calibrated).length;
	// Mirrors the backend's "unchanged" count: the location already stores this mode.
	const isSame = (l: RoutingLocationPolicy) => l.configured && l.mode === mode;
	const unchanged = locations.filter(isSame).length;

	async function onApply() {
		setSaving(true);
		const result = await sendRouting<{ changed: number; unchanged: number }>(
			url,
			"PUT",
			{
				items: locations.map((l) => ({ location_id: l.locationId, mode })),
				reason: reason.trim() || undefined,
			},
			t("messages.error"),
		);
		setSaving(false);
		if (!result.ok) {
			// All or nothing: the backend's message names the bad entry and nothing was saved.
			toast.error(result.error, { duration: Number.POSITIVE_INFINITY });
			return;
		}
		toast.success(
			t("locations.bulk.done", {
				changed: result.data.changed,
				unchanged: result.data.unchanged,
			}),
		);
		onDone();
		router.reload({ only: ["locations"] });
	}

	return (
		<Dialog open onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>
						{t("locations.bulk.title", { count: locations.length })}
					</DialogTitle>
					<DialogDescription>
						{t("locations.bulk.description", { env: env ?? "—" })}
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					<div className="overflow-hidden rounded-lg border">
						<div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
							<span className="font-medium">
								{t("locations.bulk.list_title")}
							</span>
							<span className="tabular-nums">
								{t("locations.bulk.list_summary", {
									changed: locations.length - unchanged,
									unchanged,
								})}
							</span>
						</div>
						<ul className="max-h-56 divide-y overflow-y-auto">
							{locations.map((l) => (
								<li
									key={l.locationId}
									className="flex items-center justify-between gap-3 px-3 py-2"
								>
									<div className="min-w-0">
										<div className="truncate text-sm font-medium">{l.city}</div>
										<div className="truncate text-xs text-muted-foreground">
											{l.state}
										</div>
									</div>
									<div className="flex shrink-0 items-center gap-1.5 text-xs">
										{mode === "HAVERSINE" && !l.calibrated && (
											<span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
												<TriangleAlert aria-hidden className="size-3.5" />
												<span className="sr-only sm:not-sr-only">
													{t("locations.uncalibrated")}
												</span>
											</span>
										)}
										{isSame(l) ? (
											<span className="text-muted-foreground">
												{t("locations.bulk.unchanged")}
											</span>
										) : (
											<>
												<span className="text-muted-foreground">
													{l.configured
														? t(`locations.mode_label.${l.mode}`)
														: t("locations.stored.default")}
												</span>
												<ArrowRight
													aria-hidden
													className="size-3 text-muted-foreground"
												/>
												<span className="font-medium">
													{t(`locations.mode_label.${mode}`)}
												</span>
											</>
										)}
									</div>
								</li>
							))}
						</ul>
					</div>

					<div className="flex flex-col gap-2">
						<Label>{t("global.mode")}</Label>
						<LocationModeSelect value={mode} onChange={setMode} />
					</div>

					{mode === "HAVERSINE" && uncalibrated > 0 && (
						<Alert className="border-amber-500 text-amber-700 dark:text-amber-400">
							<TriangleAlert className="size-4" />
							<AlertTitle>
								{t("locations.bulk.uncalibrated", {
									count: uncalibrated,
									total: locations.length,
								})}
							</AlertTitle>
							<AlertDescription>
								{t("locations.edit.uncalibrated.body")}
							</AlertDescription>
						</Alert>
					)}

					<ReasonField
						id="routing-bulk-reason"
						value={reason}
						onChange={setReason}
					/>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={saving}>
						{t("actions.cancel")}
					</Button>
					<Button onClick={onApply} disabled={saving}>
						{saving && <LoaderIcon className="size-4 animate-spin" />}
						{t("locations.bulk.confirm", { count: locations.length })}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
